import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { TouchscreenApiClient, type Surface, type VenueContext } from "@touchscreen/api-client";

export interface TouchscreenProviderProps {
  baseUrl: string;
  surface: Surface;
  /** `streamIdentifier`, e.g. `JUKE1234`. Resolving this is the caller's job — differs per surface. */
  venueRef: string;
  devKey?: string;
  /**
   * Boot retry backoff, in ms. Defaults (2s base, doubling to a 30s cap) suit a normal API. Against
   * a slow local API (e.g. a remote dev DB), the first couple of retries can fire while the prior
   * attempt's response is still the interesting one to read — raise `retryBaseMs` to space them out.
   */
  retryBaseMs?: number;
  retryMaxMs?: number;
  /**
   * Start this mount already treated as geofence-passed — set when the venue was just
   * location-verified a moment ago (Find Me's confirm step) or via the debug bypass. A plain
   * initial value, not kept in sync after mount: every venue switch fully remounts this provider
   * (see `App.tsx`), which is what resets the geofence-passed flag for free.
   */
  initialGeofencePassed?: boolean;
  /** Drops back to the venue picker — same reset already used for `venue_not_found`. Phone
   * only: the kiosk has no venue picker to drop back to (bolted to a wall, one venue for life),
   * so it simply omits this. */
  onResetVenue?: () => void;
  children: ReactNode;
}

const DEFAULT_RETRY_BASE_MS = 2_000;
const DEFAULT_RETRY_MAX_MS = 30_000;

function noop(): void {}

export type BootState =
  | { status: "booting" }
  | { status: "ready" }
  | { status: "error"; error: unknown };

interface TouchscreenContextValue {
  client: TouchscreenApiClient;
  surface: Surface;
  venue: VenueContext | null;
  boot: BootState;
  /** Has presence at `venue` been proven this session? Phone-only concern, but lives here so
   * every consumer (request flow, nearby-confirm) shares one flag per venue mount. */
  geofencePassed: boolean;
  setGeofencePassed: (passed: boolean) => void;
  /** Drops back to the venue picker — see `onResetVenue` above. */
  resetVenue: () => void;
}

const TouchscreenContext = createContext<TouchscreenContextValue | null>(null);

/**
 * Owns one `TouchscreenApiClient` for the lifetime of `venueRef` and runs the session grant on
 * mount. Never renders a raw error — `boot.status === "error"` is for the app shell to show a
 * neutral holding screen. Live data (`/state`, browse, search, …) is TanStack Query's job now —
 * see `useVenueStateQuery` and friends — this provider is session/identity only.
 */
export function TouchscreenProvider(props: TouchscreenProviderProps): React.JSX.Element {
  const {
    baseUrl,
    surface,
    venueRef,
    devKey,
    retryBaseMs = DEFAULT_RETRY_BASE_MS,
    retryMaxMs = DEFAULT_RETRY_MAX_MS,
    initialGeofencePassed = false,
    onResetVenue = noop,
    children,
  } = props;

  const client = useMemo(
    () => new TouchscreenApiClient({ baseUrl, surface, devKey }),
    // Recreate only if the identity of the connection itself changes — not on every render.
    [baseUrl, surface, devKey],
  );

  const [venue, setVenue] = useState<VenueContext | null>(null);
  const [boot, setBoot] = useState<BootState>({ status: "booting" });
  const [geofencePassed, setGeofencePassed] = useState(initialGeofencePassed);
  // Bumped to retrigger the boot effect with backoff — never surfaced, never gives up. A kiosk
  // is unattended, so "keep retrying forever behind a neutral screen" is the only option.
  const [retryTick, setRetryTick] = useState(0);
  const retryDelayRef = useRef(retryBaseMs);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    setBoot({ status: "booting" });
    setVenue(null);

    client
      .ensureSession(venueRef)
      .then((venueContext) => {
        if (cancelled) return;
        setVenue(venueContext);
        setBoot({ status: "ready" });
        retryDelayRef.current = retryBaseMs; // reset backoff once we're through
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setBoot({ status: "error", error });
        // Retry with backoff behind the neutral holding screen — never surface a raw error and
        // never stop trying. `retryMaxMs` caps it so recovery is still prompt once the network
        // (or, locally, a slow remote DB) catches up.
        const delay = retryDelayRef.current;
        retryDelayRef.current = Math.min(retryMaxMs, delay * 2);
        retryTimer = setTimeout(() => {
          if (!cancelled) setRetryTick((t) => t + 1);
        }, delay);
      });

    return () => {
      cancelled = true;
      if (retryTimer !== null) clearTimeout(retryTimer);
    };
  }, [client, venueRef, retryTick, retryBaseMs, retryMaxMs]);

  const value = useMemo<TouchscreenContextValue>(
    () => ({ client, surface, venue, boot, geofencePassed, setGeofencePassed, resetVenue: onResetVenue }),
    [client, surface, venue, boot, geofencePassed, onResetVenue],
  );

  return <TouchscreenContext.Provider value={value}>{children}</TouchscreenContext.Provider>;
}

export function useTouchscreenContext(): TouchscreenContextValue {
  const ctx = useContext(TouchscreenContext);
  if (!ctx) {
    throw new Error("useTouchscreenContext must be used within a <TouchscreenProvider>");
  }
  return ctx;
}
